'use client';
import React from 'react';

import classnames from 'classnames';

import { Watchlist } from '@benzinga/watchlist-manager';
import { Modal } from '@benzinga/core-ui';
import styled from '@benzinga/themetron';

interface Props {
  closeModal: () => void;
  updateWatchlist: (watchlist: Watchlist) => void;
  watchlist: Watchlist;
  watchlistName?: string;
}

interface State {
  readonly newName: string;
  readonly showReqField: boolean;
}

export const WatchlistEdit: React.FC<Props> = props => {
  const [state, setState] = React.useState<State>({
    newName: props.watchlistName || '',
    showReqField: false,
  });

  const NAME_FIELD = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (NAME_FIELD.current) {
      NAME_FIELD.current.focus();
      NAME_FIELD.current.setSelectionRange(0, NAME_FIELD.current.value.length);
    }
  }, []);

  const onChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      newName: event.currentTarget.value,
      showReqField: false,
    });
  }, []);

  const doUpdate = React.useCallback(() => {
    const newName = state.newName.trim();
    if (!newName?.length) {
      setState(old => ({ ...old, showReqField: true }));
    } else {
      if (newName !== props.watchlistName) {
        const updateWatchlist = props.updateWatchlist;
        updateWatchlist({ ...props.watchlist, name: newName });
      }
      const closeModal = props.closeModal;
      closeModal();
    }
  }, [props.closeModal, props.updateWatchlist, props.watchlist, props.watchlistName, state.newName]);

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        doUpdate();
      }
    },
    [doUpdate],
  );

  const { newName, showReqField } = state;
  const { closeModal, watchlistName } = props;
  const buttonDisabled = !newName?.length;

  return (
    <Modal
      onClose={closeModal}
      size={{ alert: true }}
      title={
        <ModalTitleSpan>
          Edit <span className="u-primaryText pre">{watchlistName}</span>
        </ModalTitleSpan>
      }
    >
      <div className="Modal-content">
        <label className={classnames('Modal-label', { 'Modal-label--warning': showReqField })}>
          Edit Watchlist Name
        </label>

        <input
          className={classnames('Modal-input', 'Modal-input--text', { 'Input--warning': showReqField })}
          onChange={onChange}
          onKeyDown={onKeyDown}
          ref={NAME_FIELD}
          type="text"
          value={newName}
        />

        <div className="Modal-proceed">
          <button className="Button Button--text" onClick={closeModal}>
            Cancel
          </button>
          <button
            className={classnames('Button', {
              'Button--disabled': buttonDisabled,
              'Button--primary': !buttonDisabled,
            })}
            onClick={doUpdate}
          >
            Update
          </button>
        </div>
      </div>
    </Modal>
  );
};

const ModalTitleSpan = styled.span`
  max-width: 100%;
  vertical-align: bottom;
  .u-primaryText {
    display: inline-block;
    max-width: 90%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    vertical-align: inherit;
  }
`;
